/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthStateService } from '../../core/services/auth-state.service';
import { SessionExpiredError } from '../../core/services/session-expired.error';
import { UserLevel } from '../enums/user-level.enum';
import { User } from '../models/user/user.model';
import {
  UserForm,
  UserFormField,
} from '../models/userForm/user-form.model.form';
import {
  isBlockingSeniorityDateField,
  isSeniorityDateField,
  isValidDateInDdMmYyyyFormat,
} from '../utilities/date-validator';
import { DialogService } from './dialog.service';
import { FormCompletedError } from './form-completed.error';

interface FormStep {
  index: number;
  name: string;
}
interface ActivityField {
  name: string;
  value: string;
}

function dateFormatValidator(control: AbstractControl) {
  return !control.value || isValidDateInDdMmYyyyFormat(control.value)
    ? null
    : { invalidDateFormat: true };
}

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private formSteps: FormStep[] = [
    { index: 0, name: 'personal_info' },
    { index: 1, name: 'performance' },
    { index: 2, name: 'conclusion' },
  ];

  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);
  private readonly authStateService = inject(AuthStateService);

  protected readonly currentUser = this.authStateService.user();

  constructor(
    private httpClient: HttpClient,
    private formBuilder: FormBuilder,
  ) {}

  private getStepName(index: number): string {
    const step = this.formSteps.find((step) => step.index === index);
    return step?.name ?? '';
  }

  getStepIndex(name: string): number | undefined {
    const step = this.formSteps.find((step) => step.name === name);
    return step?.index;
  }

  public getFormId(index: number) {
    const params = new HttpParams().set('page', this.getStepName(index));
    return this.httpClient
      .get<any>(`${environment.apiUrl}/forms`, { params })
      .pipe(
        map((response: any) => {
          return response?.result?.id;
        }),
      );
  }

  public getPreviousForm(index: number) {
    const params = new HttpParams().set('page', this.getStepName(index));
    return this.httpClient
      .get<any>(`${environment.apiUrl}/forms`, { params })
      .pipe(
        map((response: any) => {
          return response?.result?.questions || {};
        }),
      );
  }

  public getFormState(index: number) {
    const params = new HttpParams().set('page', this.getStepName(index));
    return this.httpClient
      .get<any>(`${environment.apiUrl}/forms`, { params })
      .pipe(map((response: any) => response?.result));
  }

  public getForm(index: number) {
    const params = new HttpParams().set('page', this.getStepName(index));
    return this.httpClient
      .get<any>(`${environment.apiUrl}/forms`, { params })
      .pipe(
        map((response: any) => {
          return response?.result;
        }),
      );
  }

  public getWorkingYears(user: User): number {
    if (this.isSingleNoteFieldUser(user)) {
      return 1;
    }

    let numberOfYears = 0;
    switch (user.details?.level) {
      case UserLevel.I2:
      case UserLevel.P2:
        numberOfYears = 3;
        break;
      case UserLevel.P3:
      case UserLevel.I3:
        numberOfYears = 5;
        break;
      case UserLevel.P4:
      case UserLevel.I4:
        numberOfYears = 7;
        break;
      default:
        numberOfYears = 2;
        break;
    }

    return numberOfYears;
  }

  public isSingleNoteFieldUser(user: User | null): boolean {
    return this.isSingleNoteFieldLevel(user?.details?.level);
  }

  public isTechnicalAdministrativeUser(user: User | null): boolean {
    switch (user?.details?.level) {
      case UserLevel.I1:
      case UserLevel.I2:
      case UserLevel.I3:
      case UserLevel.I4:
      case UserLevel.P1:
      case UserLevel.P2:
      case UserLevel.P3:
      case UserLevel.P4:
        return false;
      default:
        return true;
    }
  }

  private isSingleNoteFieldLevel(level: string | null | undefined): boolean {
    switch (level) {
      case UserLevel.A1:
      case UserLevel.A2:
      case UserLevel.A3:
      case UserLevel.A4:
      case UserLevel.B1:
      case UserLevel.B2:
      case UserLevel.B3:
      case UserLevel.B4:
      case UserLevel.C1:
      case UserLevel.C2:
      case UserLevel.C3:
      case UserLevel.C4:
      case UserLevel.C5:
      case UserLevel.IST3:
      case UserLevel.IST4:
      case UserLevel.IST5:
      case UserLevel.ISPTI3:
      case UserLevel.IVC3:
      case UserLevel.IVC4:
      case UserLevel.IVC5:
        return true;
      default:
        return false;
    }
  }

  updateForm(values: any, answerId?: string) {
    return this.getFormId(values.index + 1).pipe(
      switchMap((formId) => {
        let url = `${environment.apiUrl}/forms/${formId}`;
        if (answerId) {
          url += `/${answerId}`;
        }
        return this.httpClient.put<any>(url, values).pipe(
          tap((response) => {
            this.dialogService.showSuccess(
              this.translateService.instant('USER_PAGE.FORM.UPDATE_SUCCESSFUL'),
            );
          }),
          map((response) => response?.result ?? answerId ?? null),
          catchError((error) => {
            if (error instanceof SessionExpiredError) {
              return throwError(() => error);
            }
            if (isFormCompletedError(error)) {
              return throwError(() => new FormCompletedError(undefined, error));
            }
            this.dialogService.showError(
              this.translateService.instant('USER_PAGE.FORM.UPDATE_ERROR'),
            );
            return of(null);
          }),
        );
      }),
      catchError((error) => {
        if (
          error instanceof SessionExpiredError ||
          error instanceof FormCompletedError
        ) {
          return throwError(() => error);
        }
        this.dialogService.showError(
          this.translateService.instant('USER_PAGE.FORM.UPDATE_ERROR'),
        );
        return of(null);
      }),
    );
  }

  submitForm(values: any): Observable<boolean> {
    return this.getFormId(values.index + 1).pipe(
      switchMap((formId) =>
        this.httpClient
          .post<any>(`${environment.apiUrl}/forms/${formId}`, values)
          .pipe(
            tap((response) => {
              this.dialogService.showSuccess(
                this.translateService.instant(
                  'USER_PAGE.FORM.SUBMIT_SUCCESSFUL',
                ),
              );
            }),
            map(() => true),
            catchError((error) => {
              if (error instanceof SessionExpiredError) {
                return throwError(() => error);
              }
              if (isFormCompletedError(error)) {
                return throwError(
                  () => new FormCompletedError(undefined, error),
                );
              }
              this.dialogService.showError(
                this.translateService.instant('USER_PAGE.FORM.SUBMIT_ERROR'),
              );
              return of(false);
            }),
          ),
      ),
      catchError((error) => {
        if (
          error instanceof SessionExpiredError ||
          error instanceof FormCompletedError
        ) {
          return throwError(() => error);
        }
        this.dialogService.showError(
          this.translateService.instant('USER_PAGE.FORM.SUBMIT_ERROR'),
        );
        return of(false);
      }),
    );
  }

  buildUserForm(userForm: UserForm): FormGroup {
    const form = this.formBuilder.group({
      index: this.getStepIndex(userForm.name),
      forms: this.formBuilder.array(
        userForm.forms.map((form) =>
          this.formBuilder.group({
            form_name: form.form_name,
            section_name: form.section_name,
            page_name: form.page_name,
            fields: this.formBuilder.array(
              form.fields.map((field) => {
                const canEdit = this.canEditField(field);
                const form = this.formBuilder.group({
                  name: field.name,
                  value: this.formBuilder.control(
                    this.fillExperienceForm(field.name),
                    [
                      ...(field.required ? [Validators.required] : []),
                      Validators.maxLength(4000),
                      ...(isSeniorityDateField(field.name)
                        ? [dateFormatValidator]
                        : []),
                    ],
                  ),
                  label: field.label,
                  can_edit: canEdit,
                  required: this.formBuilder.control(field.required),
                  notes: this.formBuilder.group({
                    disabled: this.formBuilder.control(field.notes.disabled),
                    text: this.formBuilder.control(
                      field.notes.text || '',
                      canEdit
                        ? [
                            Validators.required,
                            ...(field.notes.maxLength
                              ? [Validators.maxLength(field.notes.maxLength)]
                              : []),
                          ]
                        : [],
                    ),
                  }),
                });
                if (field.disabled) {
                  form.disable();
                }
                return form;
              }),
            ),
          }),
        ),
      ),
    });

    form.addValidators((control) =>
      this.hasInvalidBlockingSeniorityDate(control)
        ? { invalidSeniorityDate: true }
        : null,
    );
    form.updateValueAndValidity();

    return form;
  }

  public isSeniorityDateField(fieldName: unknown): boolean {
    return isSeniorityDateField(fieldName);
  }

  public isValidSeniorityDate(value: unknown): boolean {
    return isValidDateInDdMmYyyyFormat(value);
  }

  public fillExperienceForm(fieldName: string): string {
    if (fieldName === 'anzianita-pubblica-amministrazione') {
      return (
        this.currentUser?.details?.publicAdministrationSeniority ??
        this.getTodayAsString()
      );
    } else if (fieldName === 'data-assunzione-in-enac') {
      return (
        this.currentUser?.details?.recruitmentDate ?? this.getTodayAsString()
      );
    } else if (fieldName === 'anzianita-categoria-attuale') {
      return (
        this.currentUser?.details?.currentCategorySeniority ??
        this.getTodayAsString()
      );
    } else if (fieldName === 'anzianita-servizio') {
      return (
        this.currentUser?.details?.currentLevelSeniority ??
        this.getTodayAsString()
      );
    }

    return '';
  }

  private canEditField(field: UserFormField): boolean {
    if (!field.canEdit) {
      return false;
    }

    return (
      !this.isServiceSeniorityField(field.name) ||
      this.isTechnicalAdministrativeUser(this.currentUser)
    );
  }

  private hasInvalidBlockingSeniorityDate(control: AbstractControl): boolean {
    const forms = control.get('forms');
    if (!(forms instanceof FormArray)) {
      return false;
    }

    return forms.controls.some((form) => {
      const fields = form.get('fields');
      if (!(fields instanceof FormArray)) {
        return false;
      }

      return fields.controls.some((field) => {
        const name = field.get('name')?.value;
        const value = field.get('value')?.value;
        return (
          isBlockingSeniorityDateField(name) &&
          !isValidDateInDdMmYyyyFormat(value)
        );
      });
    });
  }

  private isServiceSeniorityField(fieldName: string): boolean {
    return [
      'anzianita-pubblica-amministrazione',
      'data-assunzione-in-enac',
      'anzianita-categoria-attuale',
    ].includes(fieldName);
  }

  private getTodayAsString(): string {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  }
}

function isFormCompletedError(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 409;
}
